import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { StandardEvents } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * Keeps each variant-picker option button's displayed price in sync with the quantity
 * currently entered on the PDP, resolving the same mnml.volume_pricing_tiers metafield
 * (percentage vs. fixed) that bbd-tier-price.js / bbd-total-price.js already use.
 *
 * Each price span carries its own matched variant's base price and tiers (data-variant-price /
 * data-tiers), since different option buttons can resolve to different variants with
 * independent tier data.
 *
 * `updateAll` is public (not `#`) so assets/bbd-b2b-options.js can re-run the same
 * quantity/tier formatting after it fixes up a price span that Liquid never rendered (or
 * rendered against a stale hidden option) - reusing this component's tier-lookup rather than
 * duplicating it.
 *
 * @extends {Component}
 */
class BbdVariantOptionPricesComponent extends Component {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    this.#attachEventListeners();
    this.updateAll(this.#getCurrentQuantity());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  #attachEventListeners() {
    const { signal } = this.#abortController;
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
  }

  /**
   * Handles quantity selector updates. variant-picker and product-form-component are
   * sibling blocks (not ancestor/descendant), so scope by shared section instead of
   * closest('product-form-component'), and read the quantity straight off the event.
   * @param {import('@theme/events').QuantitySelectorUpdateEvent} event
   */
  #handleQuantityUpdate = (event) => {
    if (!(event.target instanceof Element)) return;

    const mySection = this.closest('.shopify-section, dialog');
    const eventSection = event.target.closest('.shopify-section, dialog');
    if (!mySection || mySection !== eventSection) return;

    this.updateAll(event.detail?.quantity ?? this.#getCurrentQuantity());
  };

  /**
   * Handles variant-switch events. variant-picker.js morphs this element's price spans
   * (data-variant-price / data-tiers) to the new variant's values *before* this promise
   * resolves, so no manual data copying is needed here - just re-run the existing
   * recompute against the already-updated attributes for the still-entered quantity.
   * @param {import('@shopify/events').ProductSelectEvent} event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      ?.then(() => this.updateAll(this.#getCurrentQuantity()))
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[bbd-variant-option-prices] Product select promise rejected:', error);
        }
      });
  };

  /**
   * @returns {number}
   */
  #getCurrentQuantity() {
    const section = this.closest('.shopify-section, dialog');
    const quantityInput = /** @type {HTMLInputElement | null} */ (
      section?.querySelector('product-form-component input[name="quantity"]')
    );
    return parseInt(quantityInput?.value ?? '') || 1;
  }

  /**
   * @param {number} [quantity]
   */
  updateAll(quantity = this.#getCurrentQuantity()) {
    this.querySelectorAll('[data-key="variant-option-price"]').forEach((span) => {
      this.#updateOne(/** @type {HTMLElement} */ (span), quantity);
    });
  }

  /**
   * @param {HTMLElement} span
   * @param {number} quantity
   */
  #updateOne(span, quantity) {
    const basePrice = parseInt(span.dataset.variantPrice ?? '');
    if (isNaN(basePrice)) return;

    let price = basePrice;

    if (span.dataset.tiers) {
      const tiers = JSON.parse(span.dataset.tiers).sort((a, b) => b.qty - a.qty);
      const tier = tiers.find((pb) => quantity >= pb.qty);

      if (tier) {
        price = tier.type === 'percentage' ? basePrice - Math.round((basePrice * tier.price) / 100) : tier.price;
      }
    }

    const amountEl = span.querySelector('[data-key="variant-option-price-amount"]');
    if (!amountEl) return;

    const { moneyFormat, currency } = this.dataset;
    amountEl.textContent = formatMoney(price, moneyFormat || '{{amount}}', currency || '');
  }
}

if (!customElements.get('bbd-variant-option-prices')) {
  customElements.define('bbd-variant-option-prices', BbdVariantOptionPricesComponent);
}
